import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Paper, 
  Grid,
  Skeleton,
  Avatar,
  useTheme,
  alpha,
  Chip
} from '@mui/material';
import {
  People as PeopleIcon,
  FitnessCenter as FitnessCenterIcon,
  Event as EventIcon,
  BookmarkBorder as BookingIcon,
  Settings as SettingsIcon,
  Assessment as AssessmentIcon,
  Notifications as NotificationsIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  SupervisorAccount as AdminIcon,
  AccessTime as SessionIcon,
  Group as MemberTypeIcon,
  Policy as QuotaIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import MUIStatCard from './ui/MUIStatCard';
import { useNotifications } from '../utils/featureFlags';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalFacilities: 0,
    totalClasses: 0,
    activeBookings: 0,
    pendingRegistrations: 0,
    pendingPayments: 0,
    thisMonthRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const notificationsEnabled = useNotifications();

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch all data in parallel
      const [
        usersResponse,
        facilitiesResponse,
        classesResponse,
        bookingsResponse,
        paymentStatsResponse,
        verificationStatsResponse
      ] = await Promise.all([
        api.get('/users'),
        api.get('/facilities/all'),
        api.get('/scheduled-classes'),
        api.get('/bookings/admin/all'),
        api.get('/admin/payments/statistics'),
        api.get('/admin/verification/statistics')
      ]);

      const totalUsers = usersResponse.data.data.length;
      const totalFacilities = facilitiesResponse.data.data.length;
      const totalClasses = classesResponse.data.data.length;
      const activeBookings = bookingsResponse.data.data.filter(
        booking => booking.status === 'confirmed'
      ).length;
      
      // Get real pending data
      const pendingRegistrations = verificationStatsResponse.data.data.overall.pendingVerification || 0;
      const pendingPayments = paymentStatsResponse.data.data.overview.pendingPayments || 0;
      
      // Get real revenue data
      const thisMonthRevenue = paymentStatsResponse.data.data.revenue.monthly || 0;

      setStats({
        totalUsers,
        totalFacilities,
        totalClasses,
        activeBookings,
        pendingRegistrations,
        pendingPayments,
        thisMonthRevenue
      });

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const theme = useTheme();

  const statsCards = [
    {
      title: t('admin:dashboard.stats.totalUsers'),
      value: stats.totalUsers,
      icon: <PeopleIcon />,
      onClick: () => navigate('/admin/users')
    },
    {
      title: t('admin:dashboard.stats.facilities'),
      value: stats.totalFacilities,
      icon: <FitnessCenterIcon />,
      onClick: () => navigate('/admin/facilities')
    },
    {
      title: t('admin:dashboard.stats.classes'),
      value: stats.totalClasses,
      icon: <EventIcon />,
      onClick: () => navigate('/admin/classes')
    },
    {
      title: t('admin:dashboard.stats.activeBookings'),
      value: stats.activeBookings,
      icon: <BookingIcon />,
      onClick: () => navigate('/admin/bookings')
    },
    {
      title: t('admin:dashboard.stats.pendingRegistrations'),
      value: stats.pendingRegistrations,
      icon: notificationsEnabled ? <NotificationsIcon /> : <AdminIcon />,
      onClick: () => navigate('/admin/verification'),
      isAlert: stats.pendingRegistrations > 0,
      alertColor: '#f44336'
    },
    {
      title: t('admin:dashboard.stats.pendingPayments'),
      value: stats.pendingPayments,
      icon: <PaymentIcon />,
      onClick: () => navigate('/admin/payments'),
      isAlert: stats.pendingPayments > 0,
      alertColor: '#f44336'
    },
    {
      title: t('admin:dashboard.stats.monthlyRevenue'),
      value: `₺${stats.thisMonthRevenue.toLocaleString()}`,
      icon: <TrendingUpIcon />,
      trend: t('admin:dashboard.stats.thisMonth'),
      trendDirection: "up"
    }
  ];

  const quickActions = [
    {
      title: t('admin:dashboard.quickActions.manageUsers.title'),
      description: t('admin:dashboard.quickActions.manageUsers.description'),
      icon: <PeopleIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/users'),
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.3)
    },
    {
      title: t('admin:dashboard.quickActions.manageFacilities.title'),
      description: t('admin:dashboard.quickActions.manageFacilities.description'),
      icon: <FitnessCenterIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/facilities'),
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.3)
    },
    {
      title: t('admin:dashboard.quickActions.scheduleClasses.title'),
      description: t('admin:dashboard.quickActions.scheduleClasses.description'),
      icon: <ScheduleIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/scheduled-classes'),
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.3)
    },
    {
      title: t('admin:dashboard.quickActions.systemSettings.title'),
      description: t('admin:dashboard.quickActions.systemSettings.description'),
      icon: <SettingsIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/system-settings'),
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.3)
    },
    ...(notificationsEnabled ? [{
      title: t('admin:dashboard.quickActions.notifications.title'),
      description: t('admin:dashboard.quickActions.notifications.description'),
      icon: <NotificationsIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/notifications'),
      color: theme.palette.error.main,
      bgColor: alpha(theme.palette.error.main, 0.3)
    }] : []),
    {
      title: t('admin:dashboard.quickActions.sessionQuotas.title'),
      description: t('admin:dashboard.quickActions.sessionQuotas.description'),
      icon: <SessionIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/session-quotas'),
      color: theme.palette.purple?.main || '#9c27b0',
      bgColor: alpha(theme.palette.purple?.main || '#9c27b0', 0.1)
    },
    {
      title: t('admin:dashboard.quickActions.facilityQuotas.title'),
      description: t('admin:dashboard.quickActions.facilityQuotas.description'),
      icon: <QuotaIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/quotas'),
      color: theme.palette.info.main,
      bgColor: alpha(theme.palette.info.main, 0.1)
    },
    {
      title: t('admin:dashboard.quickActions.memberTypes.title'),
      description: t('admin:dashboard.quickActions.memberTypes.description'),
      icon: <MemberTypeIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/member-types'),
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    {
      title: t('admin:dashboard.quickActions.userVerification.title'),
      description: t('admin:dashboard.quickActions.userVerification.description'),
      icon: <MemberTypeIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/verification'),
      color: theme.palette.warning.main,
      bgColor: alpha(theme.palette.warning.main, 0.1)
    },
    {
      title: t('admin:dashboard.quickActions.managePlans.title'),
      description: t('admin:dashboard.quickActions.managePlans.description'),
      icon: <AssessmentIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/plans'),
      color: theme.palette.success.main,
      bgColor: alpha(theme.palette.success.main, 0.1)
    },
    {
      title: t('admin:dashboard.quickActions.paymentManagement.title'),
      description: t('admin:dashboard.quickActions.paymentManagement.description'),
      icon: <PaymentIcon sx={{ fontSize: 28 }} />,
      action: () => navigate('/admin/payments'),
      color: theme.palette.primary.main,
      bgColor: alpha(theme.palette.primary.main, 0.1)
    },
  ];

  const DashboardSkeleton = () => (
    <Box sx={{ width: '100%', px: 2, py: 2 }}>
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width="250px" height={40} />
        <Skeleton variant="text" width="350px" height={24} sx={{ mt: 1 }} />
      </Box>
      
      {/* Stats skeleton */}
      <Grid container spacing={3} sx={{ mb: 6, width: '100%' }}>
        {[...Array(4)].map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" height={40} width="60%" sx={{ mx: 'auto', mb: 1 }} />
              <Skeleton variant="text" width="80%" sx={{ mx: 'auto' }} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Content skeleton */}
      <Grid container spacing={3} sx={{ width: '100%' }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
            <Skeleton variant="text" width="150px" height={32} sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              {[...Array(4)].map((_, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      <Skeleton variant="circular" width={32} height={32} />
                      <Box sx={{ flexGrow: 1 }}>
                        <Skeleton variant="text" width="80%" />
                        <Skeleton variant="text" width="60%" />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Skeleton variant="text" width="150px" />
              <Skeleton variant="text" width="60px" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {[...Array(4)].map((_, index) => (
                <Box key={index} sx={{ pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Skeleton variant="text" width="100%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              ))}
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Skeleton variant="rectangular" width="100%" height={36} />
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  if (loading) {
    return <DashboardSkeleton />;
  }

  return (
    <Box sx={{ width: '100%', px: 1, py: 2 }}>
      <Grid container spacing={1} sx={{ width: '100%' }}>
        {/* Main Content - Full width dashboard */}
        <Grid size={{ xs: 12 }}>
          {/* Welcome Section - UPDATED LAYOUT */}
          <Box sx={{ 
            mb: 2, 
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 50%)`,
            borderRadius: 3,
            p: 2,
            position: 'relative',
            overflow: 'hidden',
          }}>
            <Box sx={{ 
              position: 'absolute',
              top: -10,
              right: -10,
              width: 100,
              height: 100,
              borderRadius: '50%',
              background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.secondary.main, 0.1)})`,
            }} />
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  mr: 3,
                  bgcolor: theme.palette.primary.main,
                  fontSize: '1.5rem'
                }}
              >
                <AdminIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'primary.main' }}>
                  {t('admin:dashboard.title')}
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                  {t('admin:dashboard.subtitle')}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              <Chip 
                icon={<TrendingUpIcon />}
                label={t('admin:dashboard.chips.revenueThisMonth', { amount: stats.thisMonthRevenue.toLocaleString() })}
                variant="outlined"
                color="success"
                size="small"
              />
              {notificationsEnabled && (
                <Chip 
                  icon={<NotificationsIcon />}
                  label={t('admin:dashboard.chips.pendingRegistrations', { count: stats.pendingRegistrations })}
                  color={stats.pendingRegistrations > 0 ? "error" : "success"}
                  size="small"
                  onClick={() => navigate('/admin/verification')}
                  sx={{ cursor: 'pointer' }}
                />
              )}
              <Chip 
                icon={<PaymentIcon />}
                label={t('admin:dashboard.chips.pendingPayments', { count: stats.pendingPayments })}
                color={stats.pendingPayments > 0 ? "error" : "success"}
                size="small"
                onClick={() => navigate('/admin/payments')}
                sx={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
          
          {/* Stats Cards */}
          <Grid container spacing={2} sx={{ mb: 3, width: '100%' }}>
            {statsCards.map((card, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, xl: 2 }} key={index} sx={{ display: 'flex' }}>
                <MUIStatCard
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  loading={loading}
                  onClick={card.onClick}
                  trend={card.trend}
                  trendDirection={card.trendDirection}
                  isAlert={card.isAlert}
                  alertColor={card.alertColor}
                  sx={{
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4
                    }
                  }}
                />
              </Grid>
            ))}
          </Grid>

          {/* Quick Actions */}
          <Paper 
            elevation={2}
            variant="outlined"
            sx={{ 
              p: 4,
              borderRadius: 2,
              boxShadow: 3
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'text.primary' }}>
              {t('admin:dashboard.quickActions.title')}
            </Typography>
            {loading ? (
              <Grid container spacing={2} sx={{ width: '100%' }}>
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
                    <Box sx={{ textAlign: 'center', p: 2, minHeight: 160 }}>
                      <Skeleton variant="circular" width={56} height={56} sx={{ mx: 'auto', mb: 2 }} />
                      <Skeleton width="80%" height={20} sx={{ mx: 'auto', mb: 1 }} />
                      <Skeleton width="100%" height={16} sx={{ mx: 'auto' }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Grid container spacing={2} sx={{ width: '100%' }}>
                {quickActions.map((action, index) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2.4 }} key={index}>
                    <Paper
                      elevation={2}
                      onClick={action.action}
                      sx={{ 
                        p: 2,
                        width: '100%',
                        textAlign: 'center', 
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: `2px solid ${alpha(action.color, 0.3)}`,
                        borderRadius: 3,
                        bgcolor: alpha(action.color, 0.08),
                        minHeight: 160,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 8px 24px ${alpha(action.color, 0.25)}`,
                          borderColor: action.color,
                          bgcolor: alpha(action.color, 0.12)
                        }
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 64,
                          height: 64,
                          borderRadius: '50%',
                          bgcolor: alpha(action.color, 0.15),
                          color: action.color,
                          mb: 1,
                          boxShadow: `0 4px 12px ${alpha(action.color, 0.2)}`
                        }}
                      >
                        {action.icon}
                      </Box>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 0.5,
                          color: 'text.primary'
                        }}
                      >
                        {action.title}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: '0.75rem', lineHeight: 1.4, textAlign: 'center' }}
                      >
                        {action.description}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;